import { useMemo } from "react";

import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getMetricBranchTrend } from "@/service/reqUrl.service";

import type { MetricRelationsParams, MetricRelationsResponse, MetricTrendValue } from "./types";

interface UseMetricBranchTrendResult {
  /** One metric's trend per branch (`MetricTrendValues`, each row carrying a
   *  `BranchCode`). */
  trendValues: MetricTrendValue[];
  loading: boolean;
  error: string | null;
}

/**
 * Compare-view "Branches" mode data, via POST dashboard/metric-branch-trend
 * (`GetMetricTrendForBranchDateRange`). Given the subject metric code and the
 * filters (the compared branches under `filters.branch`), returns each branch's
 * trend series.
 * `params` is null until at least one branch is picked. Silent on failure — the
 * compare chart just shows its empty state.
 */
export function useMetricBranchTrend(
  params: MetricRelationsParams | null,
): UseMetricBranchTrendResult {
  const userKey = useUserKey();

  // Callers spread the drill's shared `relationBase` — send only what this SP
  // takes (the API rejects the rest). Keyed on the JSON so a re-created object
  // with the same content doesn't change the body.
  const paramsKey = JSON.stringify(params);
  const body = useMemo<MetricRelationsParams | null>(
    () =>
      params && {
        startDate: params.startDate,
        endDate: params.endDate,
        serviceLineSystemID: params.serviceLineSystemID,
        metricCodes: params.metricCodes,
        filters: params.filters ?? null,
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramsKey],
  );

  const { data, loading, error } = useApiQuery<MetricRelationsResponse | null>({
    queryKey: qk.branchTrend(userKey, body),
    url: getMetricBranchTrend,
    body,
    enabled: body != null,
    fallbackMessage: "Failed to fetch branch trend",
  });

  return { trendValues: data?.MetricTrendValues ?? [], loading, error };
}
